// Lookups and floor mutations shared by rules and systems. Every mutation of `state.floor` goes through here
// so the id order (ascending) and the occupancy index stay in sync.

import { indexById, insertById } from './core/sort.ts';
import type { Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { type DepositDef, type FamilyDef, isActive, type SimDefs } from './defs.ts';
import type { FamId, OreId } from './gen/ids.ts';
import { occAt, occSet } from './occupancy.ts';
import type { Building, DepositB, FloorItem, SimState } from './state.ts';

export function famDef(d: SimDefs, fam: FamId): FamilyDef {
  for (const f of d.families) if (f.id === fam) return f;
  throw new RangeError(`unknown family ${fam}`);
}

export function depositDef(d: SimDefs, ore: OreId): DepositDef {
  for (const x of d.deposits) if (x.id === ore) return x;
  throw new RangeError(`unknown ore ${ore}`);
}

/** True for a family/tier that exists in the defs' content version. */
export function isValidItem(d: SimDefs, fam: FamId, tier: Int): boolean {
  for (const f of d.families) {
    if (f.id === fam) {
      return isActive(d, f.version) && Number.isInteger(tier) && tier >= 1 && tier <= f.maxTier;
    }
  }
  return false;
}

/** Explicit value table (§0.1): never recomputed. */
export function itemValue(d: SimDefs, fam: FamId, tier: Int): Int {
  const v = famDef(d, fam).values[tier - 1];
  if (v === undefined) throw new RangeError(`no value for ${fam} T${tier}`);
  return v;
}

export function findItem(s: SimState, id: Int): FloorItem | null {
  const i = indexById(s.floor, id);
  return i < 0 ? null : (s.floor[i] as FloorItem);
}

export function findBuilding(s: SimState, id: Int): Building | null {
  const i = indexById(s.buildings, id);
  return i < 0 ? null : (s.buildings[i] as Building);
}

export function itemAt(c: Ctx, x: Int, y: Int): FloorItem | null {
  const id = occAt(c.occ, x, y);
  return id > 0 ? findItem(c.s, id) : null;
}

export function buildingAt(c: Ctx, x: Int, y: Int): Building | null {
  const id = occAt(c.occ, x, y);
  return id > 0 ? findBuilding(c.s, id) : null;
}

export function depositAt(c: Ctx, x: Int, y: Int): DepositB | null {
  const b = buildingAt(c, x, y);
  return b !== null && b.type === 'deposit' ? b : null;
}

export function newId(s: SimState): Int {
  const id = s.nextId;
  s.nextId = id + 1;
  return id;
}

/** Marks fam/tier as made; true the first time (merge.first, discover event). */
export function discover(c: Ctx, fam: FamId, tier: Int): boolean {
  const key = `${fam}:${tier}`;
  if (c.s.progress.discovered.includes(key)) return false;
  c.s.progress.discovered.push(key);
  c.ev.push({ e: 'discover', fam, tier });
  return true;
}

/** Adds a new loose item (fresh id, so the floor stays in id order). The cell must be free. */
export function addItem(c: Ctx, fam: FamId, tier: Int, x: Int, y: Int): FloorItem {
  const it: FloorItem = { id: newId(c.s), fam, tier, x, y };
  c.s.floor.push(it);
  occSet(c.occ, x, y, it.id);
  return it;
}

/** Puts back an item with its original id (sale undo), keeping id order. The cell must be free. */
export function restoreItem(c: Ctx, it: FloorItem): void {
  insertById(c.s.floor, it);
  occSet(c.occ, it.x, it.y, it.id);
}

export function removeItem(c: Ctx, id: Int): FloorItem {
  const i = indexById(c.s.floor, id);
  if (i < 0) throw new RangeError(`no floor item ${id}`);
  const it = c.s.floor[i] as FloorItem;
  c.s.floor.splice(i, 1);
  if (occAt(c.occ, it.x, it.y) === id) occSet(c.occ, it.x, it.y, 0);
  // A dragged item can vanish under the finger (e.g. a tap-target merge onto it): the drag goes stale.
  if (c.s.transient.held === id) c.s.transient.held = null;
  return it;
}

export function moveItem(c: Ctx, it: FloorItem, x: Int, y: Int): void {
  if (occAt(c.occ, it.x, it.y) === it.id) occSet(c.occ, it.x, it.y, 0);
  it.x = x;
  it.y = y;
  occSet(c.occ, x, y, it.id);
}
